import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Building, MapPin, Phone, Mail, Globe, Image, FileText,
  ArrowLeft, ArrowRight, Check, Upload, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_FEATURES = {
  free: {
    name: 'Free',
    hasLogo: false,
    hasDescription: false,
    hasWebsite: false,
    hasPhotos: false,
    maxPhotos: 0,
    descriptionLimit: 0,
  },
  bronze: {
    name: 'Bronze',
    hasLogo: true,
    hasDescription: true,
    hasWebsite: true,
    hasPhotos: false,
    maxPhotos: 0,
    descriptionLimit: 50,
  },
  silver: {
    name: 'Silver',
    hasLogo: true,
    hasDescription: true,
    hasWebsite: true,
    hasPhotos: true,
    maxPhotos: 5,
    descriptionLimit: 100,
  },
  gold: {
    name: 'Gold',
    hasLogo: true,
    hasDescription: true,
    hasWebsite: true,
    hasPhotos: true,
    maxPhotos: 20,
    descriptionLimit: 200,
  },
};

const ListBusinessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tier = searchParams.get('tier') || 'free';
  const tierFeatures = TIER_FEATURES[tier] || TIER_FEATURES.free;

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    sub_location: '',
    category_id: '',
    subcategory: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    features: [],
    logo_url: '',
    images: [],
    subscription_tier: tier,
  });

  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    // Count words in description
    const words = formData.description.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [formData.description]);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hotspots/config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.name || !formData.location_id || !formData.category_id) {
        toast.error('Please fill in all required fields');
        return false;
      }
    }
    if (stepNum === 2) {
      if (!formData.address || !formData.phone) {
        toast.error('Please provide address and phone number');
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/hotspots/businesses/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to register business');
      }

      const data = await response.json();
      toast.success('Business submitted successfully!');
      
      if (tier === 'free') {
        navigate('/hotspots/listing-submitted?status=pending');
      } else {
        navigate(`/hotspots/listing-submitted?status=active&id=${data.id}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit business. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLocation = config?.locations?.find(l => l.id === formData.location_id);
  const selectedCategory = config?.categories?.find(c => c.id === formData.category_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/hotspots/advertise"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-sky-600 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to pricing
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">List Your Business</h1>
              <p className="text-slate-600">
                {tierFeatures.name} Plan - {tier === 'free' ? 'Basic listing' : 'Enhanced listing with branding'}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-bold ${
              tier === 'gold' ? 'bg-yellow-100 text-yellow-800' :
              tier === 'silver' ? 'bg-slate-100 text-slate-800' :
              tier === 'bronze' ? 'bg-amber-100 text-amber-800' :
              'bg-slate-100 text-slate-600'
            }`}>
              {tierFeatures.name}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="h-4 w-4" /> : s}
                </div>
                <span className={`ml-2 text-sm ${step >= s ? 'text-slate-900' : 'text-slate-500'}`}>
                  {s === 1 ? 'Business Info' : s === 2 ? 'Contact Details' : 'Review'}
                </span>
                {s < 3 && <div className={`w-16 h-1 mx-4 rounded ${step > s ? 'bg-sky-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          
          {/* Step 1: Business Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Business Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Pacific Realty Gold Coast"
                  className="text-lg"
                  data-testid="business-name-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    City/Region *
                  </label>
                  <Select value={formData.location_id} onValueChange={(v) => handleChange('location_id', v)}>
                    <SelectTrigger data-testid="location-select">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.locations?.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Suburb/Area
                  </label>
                  <Select 
                    value={formData.sub_location} 
                    onValueChange={(v) => handleChange('sub_location', v)}
                    disabled={!selectedLocation?.sub_locations?.length}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select suburb" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedLocation?.sub_locations?.map((sub) => (
                        <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Category *
                  </label>
                  <Select value={formData.category_id} onValueChange={(v) => handleChange('category_id', v)}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.categories?.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Subcategory
                  </label>
                  <Select 
                    value={formData.subcategory} 
                    onValueChange={(v) => handleChange('subcategory', v)}
                    disabled={!selectedCategory?.subcategories?.length}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCategory?.subcategories?.map((sub) => (
                        <SelectItem key={sub.slug} value={sub.slug}>{sub.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {tierFeatures.hasDescription && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Business Description ({tierFeatures.descriptionLimit} words max)
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Tell customers what makes your business special..."
                    rows={4}
                    data-testid="description-input"
                  />
                  <p className={`text-sm mt-1 ${wordCount > tierFeatures.descriptionLimit ? 'text-red-500' : 'text-slate-500'}`}>
                    {wordCount}/{tierFeatures.descriptionLimit} words
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Contact Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Street Address *
                </label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="123 Main Street, Suburb"
                  data-testid="address-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Phone Number *
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="07 5555 1234"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="info@yourbusiness.com.au"
                  />
                </div>
              </div>

              {tierFeatures.hasWebsite && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Globe className="inline h-4 w-4 mr-1" />
                    Website URL
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://www.yourbusiness.com.au"
                  />
                </div>
              )}

              {tierFeatures.hasLogo && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Image className="inline h-4 w-4 mr-1" />
                    Business Logo URL
                  </label>
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Provide a direct link to your logo image (PNG or JPG)
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-900">Review Your Listing</h3>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Business Name</span>
                  <span className="font-medium text-slate-900">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Location</span>
                  <span className="font-medium text-slate-900">
                    {selectedLocation?.name}{formData.sub_location ? `, ${formData.sub_location}` : ''}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Category</span>
                  <span className="font-medium text-slate-900">{selectedCategory?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Phone</span>
                  <span className="font-medium text-slate-900">{formData.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Address</span>
                  <span className="font-medium text-slate-900">{formData.address}</span>
                </div>
                {formData.email && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Email</span>
                    <span className="font-medium text-slate-900">{formData.email}</span>
                  </div>
                )}
                {formData.website && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Website</span>
                    <span className="font-medium text-slate-900 truncate max-w-[200px]">{formData.website}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">Plan</span>
                  <span className={`font-bold ${
                    tier === 'gold' ? 'text-yellow-600' :
                    tier === 'silver' ? 'text-slate-600' :
                    tier === 'bronze' ? 'text-amber-600' :
                    'text-slate-600'
                  }`}>
                    {tierFeatures.name}
                  </span>
                </div>
              </div>

              {formData.description && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
                  <p className="text-slate-600 bg-slate-50 rounded-lg p-4">{formData.description}</p>
                </div>
              )}

              <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                <p className="text-sm text-sky-800">
                  <strong>What happens next?</strong><br />
                  {tier === 'free' 
                    ? 'Your listing will be reviewed and published within 24-48 hours.'
                    : 'Your listing will go live immediately after submission.'
                  }
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <Button onClick={handleNextStep} className="bg-sky-600 hover:bg-sky-700">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-listing-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Submit Listing
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListBusinessPage;
